import React, {
  CSSProperties,
  KeyboardEvent,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { makePrefixer } from "@jpmorganchase/uitk-core";
import { GridColumnInfo } from "./GridColumn";
import { GridContext } from "./GridContext";
import cx from "classnames";
import {
  CellMeasure,
  clamp,
  getCellPosition,
  LeftPart,
  MiddlePart,
  RightPart,
  Scrollable,
  TopLeftPart,
  TopPart,
  TopRightPart,
  useBodyVisibleAreaTop,
  useBodyVisibleColumnRange,
  useClientMidHeight,
  useClientMidWidth,
  useColumnMove,
  useColumnRange,
  useColumnRegistry,
  useColumnResize,
  useHeadVisibleColumnRange,
  useLeftScrolledOutWidth,
  useProd,
  useRangeSelection,
  useRowModels,
  useRowSelection,
  useScrollToCell,
  useSum,
  useSumRangeWidth,
  useSumWidth,
  useVisibleColumnGroupRange,
  useVisibleRowRange,
} from "./internal";
import "./Grid.css";
import { SelectionContext } from "./SelectionContext";
import { SizingContext } from "./SizingContext";
import { LayoutContext } from "./LayoutContext";
import { EditorContext } from "./EditorContext"; // TODO remove
import { CursorContext } from "./CursorContext";
import { ColumnGroupProps } from "./ColumnGroup";
import { ColumnDragContext } from "./ColumnDragContext";
import { ColumnGhost } from "./internal/ColumnGhost";
import { ColumnDropTarget } from "./internal/ColumnDropTarget";
import { ColumnDataContext } from "./ColumnDataContext";

const withBaseName = makePrefixer("uitkGrid");

export type ColumnSeparatorType = "regular" | "none" | "groupEdge" | "pinned";
export type ColumnGroupRowSeparatorType = "first" | "regular" | "last";
export type ColumnGroupColumnSeparatorType = "regular" | "none" | "pinned";
export type GridRowSelectionMode = "single" | "multi" | "none";
export type GridCellSelectionMode = "range" | "none";

export type RowKeyGetter<T> = (row: T, index: number) => string;

export type GridColumnMoveHandler = (
  columnId: string,
  fromIndex: number,
  toIndex: number
) => void;

export interface GridProps<T = any> {
  children: ReactNode;
  /**
   * If `true`, zebra stripes are enabled (odd/even rows have alternate colours)
   * */
  zebra?: boolean;
  /**
   * If `true`, grid header is hidden.
   * */
  hideHeader?: boolean;
  /**
   * If `true`, column separators are rendered.
   * */
  columnSeparators?: boolean;
  /**
   * If `true`, separators are rendered between pinned and unpinned columns.
   * */
  pinnedSeparators?: boolean;
  /**
   * Row data objects. Sparse arrays are supported.
   * */
  rowData: T[];
  /**
   * Should return unique string for a given row data object.
   * If rowData is sparse then this function should work with undefined row data
   * objects and create keys based on row index.
   * */
  rowKeyGetter?: RowKeyGetter<T>;
  /**
   * Rows with these indices are selected by default.
   * */
  defaultSelectedRowIdxs?: number[];
  /**
   * Selected row indices for controlled mode.
   * */
  selectedRowIdxs?: number[];
  className?: string;
  style?: CSSProperties;
  /**
   * The variant to use. Options are `primary` and `secondary`. Default value is
   * `primary`. `secondary` variant changes grid background to reduce contrast.
   * */
  variant?: "primary" | "secondary";
  /**
   * Options are `single`, `multi` and `none`.
   * */
  rowSelectionMode?: GridRowSelectionMode;
  onRowSelected?: (selectedRowIdxs: number[]) => void;
  /**
   * If `true`, user will be able to move columns using drag and drop.
   * */
  columnMove?: boolean;
  onColumnMoved?: GridColumnMoveHandler;
  /**
   * Options are `range` and `none`.
   * */
  cellSelectionMode?: GridCellSelectionMode;
  onVisibleRowRangeChange?: (start: number, end: number) => void;
}

export interface GridRowModel<T> {
  key: string;
  index: number;
  data: T;
}

export interface GridColumnModel<T> {
  index: number;
  separator: ColumnSeparatorType;

  info: GridColumnInfo<T>;
}

export interface GridColumnGroupModel {
  index: number;
  data: ColumnGroupProps;
  childrenIds: string[];
  rowSeparator: ColumnGroupRowSeparatorType;
  columnSeparator: ColumnGroupColumnSeparatorType;
  colSpan: number;
}

function defaultRowKeyGetter<T>(row: T, index: number): string {
  return `${index}`;
}

export const Grid = function Grid<T>(props: GridProps<T>) {
  const {
    rowData,
    zebra,
    hideHeader,
    columnSeparators,
    pinnedSeparators = true,
    className,
    style,
    rowKeyGetter = defaultRowKeyGetter,
    children,
    defaultSelectedRowIdxs,
    selectedRowIdxs,
    variant = "primary",
    rowSelectionMode = "multi",
    onRowSelected,
    columnMove,
    onColumnMoved,
    cellSelectionMode = "none",
    onVisibleRowRangeChange,
  } = props;

  const rootRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const middleRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // What caused the latest scroll change. User means the UI (mouse events), table means the component initiated scrolling (scrollToCell was called for example)
  const [scrollSource, setScrollSource] = useState<"user" | "table">("user");
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const [scrollTop, setScrollTop] = useState<number>(0);

  const [hoverRowKey, setHoverRowKey] = useState<string | undefined>(undefined);

  const [clientWidth, setClientWidth] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);
  const [scrollBarHeight, setScrollBarHeight] = useState(0);
  const [scrollBarWidth, setScrollBarWidth] = useState(0);

  const [rowHeight, setRowHeight] = useState<number>(0);

  const [cursorRowIdx, setCursorRowIdx] = useState<number | undefined>(0);
  const [cursorColIdx, setCursorColIdx] = useState<number | undefined>(0);

  const [editMode, setEditMode] = useState<boolean>(false);
  const [initialText, setInitialText] = useState<string | undefined>(undefined);

  const resizeClient = useCallback(
    (clW: number, clH: number, sbW: number, sbH: number) => {
      setClientHeight(clH);
      setClientWidth(clW);
      setScrollBarHeight(sbH);
      setScrollBarWidth(sbW);
    },
    [setClientHeight, setClientWidth, setScrollBarHeight, setScrollBarWidth]
  );

  const {
    leftCols, // Columns pinned to left
    midCols, // Scrollable columns
    rightCols, // Columns pinned to right
    leftGroups,
    midGroups,
    rightGroups,
    contextValue,
  } = useColumnRegistry(children);

  const midColsById = useMemo(
    () =>
      new Map<string, GridColumnModel<T>>(
        midCols.map((c) => [c.info.props.id, c] as [string, GridColumnModel<T>])
      ),
    [midCols]
  );

  // Width of all columns pinned to left
  const leftWidth = useSumWidth(leftCols);
  // Width of all scrollable columns
  const midWidth = useSumWidth(midCols);
  // Width of all columns pinned to right
  const rightWidth = useSumWidth(rightCols);
  // Total width of all columns
  const totalWidth = useSum([leftWidth, midWidth, rightWidth]);

  const hasColumnGroups =
    leftGroups.length > 0 || midGroups.length > 0 || rightGroups.length > 0;

  const headRowCount = hideHeader ? 0 : hasColumnGroups ? 2 : 1; // TODO multiple group levels
  const rowCount = rowData.length;

  // Footer is not implemented yet.
  const botRowCount = 0; // TODO
  // Height of the header
  const topHeight = useProd([rowHeight, headRowCount]);
  // Height of the middle part (virtual height)
  const midHeight = useProd([rowHeight, rowCount]);
  // Height of the footer
  const botHeight = useProd([botRowCount, rowHeight]);
  // Total height of the grid (virtual)
  const totalHeight = useSum([topHeight, midHeight, botHeight]);
  // Client width of the middle part of the grid (viewport)
  const clientMidWidth = useClientMidWidth(clientWidth, leftWidth, rightWidth);
  // Unused width in the middle part, to be filled by an empty column with fake cells.
  const midGap = Math.max(0, Math.floor(clientMidWidth - midWidth));

  // Range of unpinned columns visible in the body of the grid (pinned columns are always visible).
  const bodyVisColRng = useBodyVisibleColumnRange(
    midCols,
    scrollLeft,
    clientMidWidth
  );

  const midGrpByColId = useMemo(() => {
    const m = new Map<string, GridColumnGroupModel>();
    for (let g of midGroups) {
      for (let c of g.childrenIds) {
        m.set(c, g);
      }
    }
    return m;
  }, [midGroups]);

  // Range of visible column groups.
  const visColGrpRng = useVisibleColumnGroupRange(
    bodyVisColRng,
    midCols,
    midGrpByColId,
    leftGroups.length
  );

  const visColGrps = useMemo(() => {
    return midGroups.slice(visColGrpRng.start, visColGrpRng.end);
  }, [visColGrpRng, midGroups]);

  const headVisColRng = useHeadVisibleColumnRange(
    bodyVisColRng,
    visColGrps,
    midColsById,
    leftCols.length
  );

  const bodyScrOutColWh = useLeftScrolledOutWidth(midCols, bodyVisColRng);
  const headScrOutColWh = useLeftScrolledOutWidth(midCols, headVisColRng);

  const bodyVisAreaLeft = useSum([leftWidth, bodyScrOutColWh]);
  const headVisAreaLeft = useSum([leftWidth, headScrOutColWh]);
  const clientMidHeight = useClientMidHeight(
    clientHeight,
    topHeight,
    botHeight
  );
  const visRowRng = useVisibleRowRange(
    scrollTop,
    clientMidHeight,
    rowHeight,
    rowCount
  );

  const bodyVisAreaTop = useBodyVisibleAreaTop(rowHeight, visRowRng, topHeight);

  const bodyVisibleColumns = useColumnRange(midCols, bodyVisColRng);
  const headVisibleColumns = useColumnRange(midCols, headVisColRng);
  const bodyVisColWh = useSumRangeWidth(midCols, bodyVisColRng);

  const headVisColWh = bodyVisColWh; // TODO implement groups

  const rootStyle = useMemo(
    () => ({
      ...style,
      "--grid-total-width": `${totalWidth}px`,
      "--grid-total-height": `${totalHeight}px`,
      "--grid-topPart-height": `${topHeight}px`,
      "--grid-leftPart-width": `${leftWidth}px`,
      "--grid-rightPart-width": `${rightWidth}px`,
      "--grid-bodyVisibleColumns-width": `${bodyVisColWh}px`,
      "--grid-bodyVisibleArea-top": `${bodyVisAreaTop}px`,
      "--grid-bodyVisibleArea-left": `${bodyVisAreaLeft}px`,
      "--grid-bottomHeight": `${botHeight}px`,
      "--grid-headerVisibleColumns-width": `${headVisColWh}px`,
      "--grid-headerVisibleArea-left": `${headVisAreaLeft}px`,
      "--grid-scrollBar-height": `${scrollBarHeight}px`,
      "--grid-scrollBar-width": `${scrollBarWidth}px`,
    }),
    [
      style,
      totalHeight,
      totalWidth,
      topHeight,
      leftWidth,
      rightWidth,
      botHeight,
      bodyVisColWh,
      bodyVisAreaLeft,
      bodyVisAreaTop,
      headVisColWh,
      headVisAreaLeft,
      scrollBarHeight,
      scrollBarWidth,
    ]
  );

  const onWheel: EventListener = useCallback(
    (event) => {
      let { deltaX, deltaY, shiftKey } = event as WheelEvent;
      if (deltaX === 0 && shiftKey) {
        deltaX = deltaY;
        deltaY = 0;
      }
      const s = scrollableRef.current;
      if (s) {
        s.scrollLeft += deltaX;
        s.scrollTop += deltaY;
      }
      event.preventDefault();
      event.stopPropagation();
    },
    [scrollableRef.current]
  );

  const cols = useMemo(
    () => [...leftCols, ...midCols, ...rightCols],
    [leftCols, midCols, rightCols]
  );

  const colsById = useMemo(
    () =>
      new Map<string, GridColumnModel<T>>(
        cols.map((c) => [c.info.props.id, c] as [string, GridColumnModel<T>])
      ),
    [cols]
  );

  const getColById = useCallback(
    (id: string) => {
      return colsById.get(id);
    },
    [colsById]
  );

  const columnDataContext: ColumnDataContext<T> = useMemo(
    () => ({
      getColById,
    }),
    [getColById]
  );

  const scroll = useCallback(
    (left?: number, top?: number, source?: "user" | "table") => {
      setScrollSource(source || "user");
      if (left !== undefined) {
        setScrollLeft(left);
      }
      if (top !== undefined) {
        setScrollTop(top);
      }
    },
    [setScrollLeft, setScrollTop, setScrollSource]
  );

  const scrollToCell = useScrollToCell(
    visRowRng,
    rowHeight,
    clientMidHeight,
    midCols,
    bodyVisColRng,
    clientMidWidth,
    scroll
  );

  const focusCellElement = (rowIdx: number, colIdx: number) => {
    setTimeout(() => {
      const nodeToFocus = rootRef.current?.querySelector(
        `td[data-row-index="${rowIdx}"][data-column-index="${colIdx}"]`
      );
      if (nodeToFocus) {
        (nodeToFocus as HTMLElement).focus();
      }
    }, 0);
  };

  const startEditMode = (text?: string) => {
    if (editMode || cursorRowIdx == undefined || cursorColIdx == undefined) {
      return;
    }
    const r = rowData[cursorRowIdx];
    const c = cols[cursorColIdx];
    const isEditable = !!contextValue.getEditor(c.info.props.id);
    if (isEditable) {
      setInitialText(text);
      setEditMode(true);
    }
  };

  const endEditMode = (value: string) => {
    if (!editMode) {
      return;
    }
    if (cursorColIdx == undefined) {
      console.error(`endEditMode: cursorColIdx is undefined in edit mode`);
      return;
    }
    const c = cols[cursorColIdx];
    const handler = c.info.props.onChange;
    if (cursorRowIdx == undefined) {
      console.error(`endEditMode: cursorRowIdx is undefined in edit mode`);
      return;
    }
    if (!handler) {
      console.warn(
        `onChange is not specified for editable column "${c.info.props.id}".`
      );
    } else {
      handler(rowData[cursorRowIdx], cursorRowIdx, value);
    }
    setEditMode(false);
    focusCellElement(cursorRowIdx, cursorColIdx);
  };

  const cancelEditMode = () => {
    if (!editMode) {
      return;
    }
    setEditMode(false);
    if (cursorRowIdx != null && cursorColIdx != null) {
      focusCellElement(cursorRowIdx, cursorColIdx);
    }
  };

  const {
    selRowIdxs,
    isAllSelected,
    isAnySelected,
    selectAll,
    selectRows,
    unselectAll,
    onMouseDown: onRowSelectionMouseDown,
  } = useRowSelection(
    rowKeyGetter,
    rowData,
    defaultSelectedRowIdxs,
    selectedRowIdxs,
    rowSelectionMode,
    onRowSelected
  );

  const rangeSelection = useRangeSelection(cellSelectionMode);

  const moveCursor = useCallback(
    (rowIdx?: number, colIdx?: number) => {
      cancelEditMode();
      if (rowData.length < 1 || cols.length < 1) {
        return;
      }
      rowIdx = clamp(rowIdx, 0, rowData.length - 1);
      colIdx = clamp(colIdx, 0, cols.length - 1);
      setCursorRowIdx(rowIdx);
      setCursorColIdx(colIdx);
      scrollToCell(rowIdx, colIdx);
      focusCellElement(rowIdx, colIdx);
      rangeSelection.onCursorMove({ rowIdx, colIdx });
    },
    [
      setCursorRowIdx,
      setCursorColIdx,
      rowData,
      rowKeyGetter,
      cols,
      rootRef.current,
      scrollToCell,
      endEditMode,
      rangeSelection.onCursorMove,
    ]
  );

  const rows = useRowModels(rowKeyGetter, rowData, visRowRng);

  const isLeftRaised = scrollLeft > 0;
  const isRightRaised = scrollLeft + clientMidWidth < midWidth;
  const isHeaderRaised = scrollTop > 0;

  const resizeColumn = useCallback(
    (colIdx: number, width: number) => {
      const col = cols[colIdx];
      col.info.onWidthChanged(width);
    },
    [cols]
  );

  const onResizeHandleMouseDown = useColumnResize(resizeColumn);

  const sizingContext: SizingContext = useMemo(
    () => ({
      resizeColumn,
      rowHeight,
      onResizeHandleMouseDown,
    }),
    [resizeColumn, rowHeight, onResizeHandleMouseDown]
  );

  const layoutContext: LayoutContext = useMemo(
    () => ({
      totalHeight,
      totalWidth,
      clientWidth,
      clientHeight,
    }),
    [totalHeight, totalWidth]
  );

  const editorContext: EditorContext = useMemo(
    () => ({
      initialText,
      editMode,
      startEditMode,
      endEditMode,
      cancelEditMode,
    }),
    [editMode, startEditMode, endEditMode, cancelEditMode, initialText]
  );

  const [isFocused, setFocused] = useState<boolean>(false);

  const onFocus = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      setFocused(true);
    },
    [setFocused]
  );

  const onBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      setFocused(false);
    },
    [setFocused]
  );

  const cursorContext: CursorContext = useMemo(
    () => ({
      isFocused,
      cursorRowIdx,
      cursorColIdx,
      moveCursor,
    }),
    [cursorRowIdx, cursorColIdx, moveCursor, isFocused]
  );

  const onColumnMove: GridColumnMoveHandler = (
    columnId,
    fromIndex,
    toIndex
  ) => {
    if (onColumnMoved && fromIndex !== toIndex) {
      onColumnMoved(columnId, fromIndex, toIndex);
    }
  };

  const {
    dragState,
    onColumnMoveHandleMouseDown,
    activeTarget,
    onColumnMoveCancel,
  } = useColumnMove(
    columnMove,
    rootRef,
    leftCols,
    midCols,
    rightCols,
    cols,
    scrollLeft,
    clientMidWidth,
    onColumnMove
  );

  const columnDragContext: ColumnDragContext = useMemo(
    () => ({
      columnMove,
      onColumnMoveHandleMouseDown,
    }),
    [columnMove, onColumnMoveHandleMouseDown]
  );

  const onMouseDown: MouseEventHandler<HTMLDivElement> = (event) => {
    onRowSelectionMouseDown(event);
    rangeSelection.onCellMouseDown(event);

    const target = event.target as HTMLElement;
    try {
      const [rowIdx, colIdx] = getCellPosition(target);
      if (colIdx >= 0) {
        moveCursor(rowIdx, colIdx);
      }
      // event.preventDefault();
      // event.stopPropagation();
    } catch (e) {
      // TODO
    }
  };

  const onKeyUp: KeyboardEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      const { key } = event;
      if (key === "Shift") {
        rangeSelection.onKeyboardRangeSelectionEnd();
      }
    },
    [rangeSelection.onKeyboardRangeSelectionEnd]
  );

  const editModeKeyHandler = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const { key } = event;
      switch (key) {
        case "F2":
        case "Enter":
          startEditMode();
          break;
        case "Backspace":
          startEditMode("");
          break;
        case "Escape":
          if (editMode) {
            cancelEditMode();
            break;
          } else {
            return false;
          }
        default:
          if (
            !editMode &&
            !event.ctrlKey &&
            !event.metaKey &&
            !event.altKey &&
            /^[\w\d ]$/.test(key)
          ) {
            startEditMode(key);
          } else {
            return false;
          }
      }
      event.preventDefault();
      event.stopPropagation();
      return true;
    },
    [startEditMode, editMode, cancelEditMode]
  );

  const selectionKeyHandler = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const { key } = event;
      switch (key) {
        case "Shift":
          rangeSelection.onKeyboardRangeSelectionStart({
            rowIdx: cursorRowIdx || 0,
            colIdx: cursorColIdx || 0,
          });
          break;
        case " ":
          if (event.ctrlKey) {
            if (cursorColIdx != undefined) {
              rangeSelection.selectRange({
                start: { rowIdx: 0, colIdx: cursorColIdx },
                end: { rowIdx: rowData.length, colIdx: cursorColIdx },
              });
            }
          } else {
            if (cursorRowIdx != undefined) {
              selectRows({
                rowIndex: cursorRowIdx,
                shift: false,
                meta: event.shiftKey,
              });
            }
          }
          break;
        case "a":
          if (event.ctrlKey || event.metaKey) {
            rangeSelection.selectRange({
              start: { rowIdx: 0, colIdx: 0 },
              end: { rowIdx: rowData.length, colIdx: cols.length },
            });
            selectAll();
          }
          break;
        default:
          return false;
      }
      event.preventDefault();
      event.stopPropagation();
      return true;
    },
    [
      rangeSelection.selectRange,
      rangeSelection.onKeyboardRangeSelectionStart,
      selectRows,
      selectAll,
      cursorColIdx,
      cursorRowIdx,
      rowData.length,
      cols.length,
    ]
  );

  const clipboardKeyHandler = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const { key } = event;
      if (
        key === "c" &&
        (event.ctrlKey || event.metaKey) &&
        rangeSelection.selectedCellRange
      ) {
        const { start, end } = rangeSelection.selectedCellRange;
        const c = (x: number, y: number) => x - y;
        const [minRow, maxRow] = [start.rowIdx, end.rowIdx].sort(c);
        const [minCol, maxCol] = [start.colIdx, end.colIdx].sort(c);
        const text: string[] = [];
        for (let r = minRow; r <= maxRow; ++r) {
          const row = rowData[r];
          const rowText: string[] = [];
          for (let c = minCol; c <= maxCol; ++c) {
            const col = cols[c]!;
            const cellValue = col.info.props.getValue!(row);
            rowText.push(cellValue);
          }
          text.push(rowText.join("\t"));
        }
        navigator.clipboard.writeText(text.join("\n"));
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
      return false;
    },
    [rangeSelection.selectedCellRange]
  );

  const pageSize = visRowRng.length - 1;

  const navigationKeyHandler = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const { key } = event;
      switch (key) {
        case "ArrowLeft":
          moveCursor(cursorRowIdx, (cursorColIdx || 0) - 1);
          break;
        case "ArrowRight":
          moveCursor(cursorRowIdx, (cursorColIdx || 0) + 1);
          break;
        case "ArrowUp":
          moveCursor((cursorRowIdx || 0) - 1, cursorColIdx);
          break;
        case "ArrowDown":
          moveCursor((cursorRowIdx || 0) + 1, cursorColIdx);
          break;
        case "PageUp":
          moveCursor((cursorRowIdx || 0) - pageSize, cursorColIdx);
          break;
        case "PageDown":
          moveCursor((cursorRowIdx || 0) + pageSize, cursorColIdx);
          break;
        case "Home":
          if (!event.ctrlKey) {
            moveCursor(cursorRowIdx, 0);
          } else {
            moveCursor(0, 0);
          }
          break;
        case "End":
          if (!event.ctrlKey) {
            moveCursor(cursorRowIdx, cols.length - 1);
          } else {
            moveCursor(rowData.length - 1, cols.length - 1);
          }
          break;
        case "Tab":
          if (
            !event.ctrlKey &&
            !event.metaKey &&
            !event.altKey &&
            editMode &&
            cursorColIdx != null &&
            cursorRowIdx != null
          ) {
            if (!event.shiftKey) {
              if (cursorColIdx < cols.length - 1) {
                moveCursor(cursorRowIdx, cursorColIdx + 1);
              } else {
                if (cursorRowIdx < rowData.length - 1) {
                  moveCursor(cursorRowIdx + 1, 0);
                }
              }
            } else {
              if (cursorColIdx > 0) {
                moveCursor(cursorRowIdx, cursorColIdx - 1);
              } else {
                if (cursorRowIdx > 0) {
                  moveCursor(cursorRowIdx - 1, cols.length - 1);
                }
              }
            }
          } else {
            return false;
          }
          break;
        case "Enter":
          if (
            editMode &&
            !event.ctrlKey &&
            !event.metaKey &&
            !event.altKey &&
            !event.shiftKey
          ) {
            if (cursorRowIdx == undefined) {
              moveCursor(0, 0);
            } else {
              moveCursor(cursorRowIdx + 1, cursorColIdx);
            }
          } else {
            return false;
          }
          break;
        default:
          return false;
      }
      event.preventDefault();
      event.stopPropagation();
      return true;
    },
    [moveCursor, cursorRowIdx, cursorRowIdx, cols.length, rowData.length]
  );

  const columnMoveKeyHandler = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const { key } = event;
      if (key === "Escape") {
        onColumnMoveCancel();
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
      return false;
    },
    []
  );

  const onKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (cursorColIdx != undefined && cursorRowIdx != undefined) {
        const column = cols[cursorColIdx];
        if (column.info.props.onKeyDown) {
          column.info.props.onKeyDown(event, cursorRowIdx);
        }
      }
      if (!event.isPropagationStopped()) {
        [
          navigationKeyHandler,
          clipboardKeyHandler,
          selectionKeyHandler,
          editModeKeyHandler,
          columnMoveKeyHandler,
        ].find((handler) => {
          return handler(event);
        });
      }
    },
    [
      navigationKeyHandler,
      clipboardKeyHandler,
      selectionKeyHandler,
      editModeKeyHandler,
      columnMoveKeyHandler,
    ]
  );

  const selectionContext: SelectionContext = useMemo(
    () => ({
      selRowIdxs,
      selectRows,
      isAllSelected,
      isAnySelected,
      selectAll,
      unselectAll,
      selectedCellRange: rangeSelection?.selectedCellRange,
    }),
    [
      selRowIdxs,
      selectRows,
      isAllSelected,
      isAnySelected,
      selectAll,
      unselectAll,
      rangeSelection?.selectedCellRange,
    ]
  );

  useEffect(() => {
    if (onVisibleRowRangeChange) {
      onVisibleRowRangeChange(visRowRng.start, visRowRng.end);
    }
  }, [onVisibleRowRangeChange, visRowRng]);

  return (
    <GridContext.Provider value={contextValue}>
      <LayoutContext.Provider value={layoutContext}>
        <SelectionContext.Provider value={selectionContext}>
          <ColumnDragContext.Provider value={columnDragContext}>
            <CursorContext.Provider value={cursorContext}>
              <SizingContext.Provider value={sizingContext}>
                <EditorContext.Provider value={editorContext}>
                  <ColumnDataContext.Provider value={columnDataContext}>
                    {props.children}
                    <div
                      className={cx(
                        withBaseName(),
                        {
                          [withBaseName("zebra")]: zebra,
                          [withBaseName("columnSeparators")]: columnSeparators,
                          [withBaseName("pinnedSeparators")]: pinnedSeparators,
                          [withBaseName("primaryBackground")]:
                            variant === "primary",
                          [withBaseName("secondaryBackground")]:
                            variant === "secondary",
                        },
                        className
                      )}
                      style={rootStyle}
                      ref={rootRef}
                      onKeyDown={onKeyDown}
                      onKeyUp={onKeyUp}
                      onMouseDown={onMouseDown}
                      onFocus={onFocus}
                      onBlur={onBlur}
                      data-name="grid-root"
                      role="grid"
                      aria-colcount={cols.length}
                      aria-rowcount={rowCount + headRowCount}
                      aria-multiselectable={rowSelectionMode === "multi"}
                    >
                      <CellMeasure setRowHeight={setRowHeight} />
                      <Scrollable
                        resizeClient={resizeClient}
                        scrollLeft={scrollLeft}
                        scrollTop={scrollTop}
                        scrollSource={scrollSource}
                        scroll={scroll}
                        scrollerRef={scrollableRef}
                        topRef={topRef}
                        rightRef={rightRef}
                        bottomRef={bottomRef}
                        leftRef={leftRef}
                        middleRef={middleRef}
                      />
                      {!hideHeader && leftCols.length > 0 && (
                        <TopLeftPart
                          onWheel={onWheel}
                          columns={leftCols}
                          columnGroups={leftGroups}
                          rightShadow={isLeftRaised}
                          bottomShadow={isHeaderRaised}
                        />
                      )}
                      {!hideHeader && (
                        <TopPart
                          columns={headVisibleColumns}
                          columnGroups={visColGrps}
                          topRef={topRef}
                          onWheel={onWheel}
                          midGap={midGap}
                          bottomShadow={isHeaderRaised}
                        />
                      )}
                      {!hideHeader && rightCols.length > 0 && (
                        <TopRightPart
                          onWheel={onWheel}
                          columns={rightCols}
                          columnGroups={rightGroups}
                          leftShadow={isRightRaised}
                          bottomShadow={isHeaderRaised}
                        />
                      )}
                      {leftCols.length > 0 && (
                        <LeftPart
                          leftRef={leftRef}
                          onWheel={onWheel}
                          columns={leftCols}
                          rows={rows}
                          rightShadow={isLeftRaised}
                          hoverOverRowKey={hoverRowKey}
                          setHoverOverRowKey={setHoverRowKey}
                          zebra={zebra}
                        />
                      )}
                      <MiddlePart
                        middleRef={middleRef}
                        onWheel={onWheel}
                        columns={bodyVisibleColumns}
                        rows={rows}
                        hoverOverRowKey={hoverRowKey}
                        setHoverOverRowKey={setHoverRowKey}
                        midGap={midGap}
                        zebra={zebra}
                      />
                      {rightCols.length > 0 && (
                        <RightPart
                          rightRef={rightRef}
                          onWheel={onWheel}
                          columns={rightCols}
                          rows={rows}
                          leftShadow={isLeftRaised}
                          hoverOverRowKey={hoverRowKey}
                          setHoverOverRowKey={setHoverRowKey}
                          zebra={zebra}
                        />
                      )}
                      <ColumnDropTarget x={activeTarget?.x} />
                      <ColumnGhost
                        columns={cols}
                        rows={rows}
                        dragState={dragState}
                        zebra={zebra}
                      />
                    </div>
                  </ColumnDataContext.Provider>
                </EditorContext.Provider>
              </SizingContext.Provider>
            </CursorContext.Provider>
          </ColumnDragContext.Provider>
        </SelectionContext.Provider>
      </LayoutContext.Provider>
    </GridContext.Provider>
  );
};
